import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { TrailingSlashNormalize } from "./components/TrailingSlashNormalize";
import { LocaleProvider } from "./locale/LocaleProvider";
import { appRoutes } from "./lib/appRoutes";
import { legacyRedirects } from "./lib/appRoutePaths";
import { LocaleAutoRedirect } from "./components/LocaleAutoRedirect";
import { LegacyRuRedirect } from "./components/LegacyRuRedirect";
import { LocaleUrlSync } from "./components/LocaleUrlSync";
import { ScrollToTop } from "./components/ScrollToTop";
import { localizedPath } from "./locale/routes";
import type { AppLang } from "./locale/appLang";

const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const routeFallback = (
  <div className="min-h-[40vh] bg-[#0b0f14]" aria-busy="true">
    <span className="sr-only">Loading</span>
  </div>
);

function langRoutes(lang: AppLang) {
  return (
    <>
      {appRoutes.map(({ path, element }) => (
        <Route
          key={`${lang}-${path}`}
          path={path === "/" && lang === "ru" ? "/" : localizedPath(path, lang)}
          element={element}
        />
      ))}
      {legacyRedirects.map(({ from, to }) => (
        <Route
          key={`${lang}-redir-${from}`}
          path={localizedPath(from, lang)}
          element={<Navigate to={localizedPath(to, lang)} replace />}
        />
      ))}
    </>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <LocaleProvider>
        <LegacyRuRedirect />
        <LocaleAutoRedirect />
        <LocaleUrlSync />
        <ScrollToTop />
        <TrailingSlashNormalize />
        <Suspense fallback={routeFallback}>
          <Routes>
            {langRoutes("ru")}
            {langRoutes("uk")}
            {langRoutes("kk")}
            {langRoutes("uz")}
            {langRoutes("ky")}
            <Route
              path="/udalennое-administrirovanie-serverov"
              element={<Navigate to={localizedPath("/services/retainer", "ru")} replace />}
            />
            <Route
              path="/udalennое-administrirovanie-serverov/"
              element={<Navigate to={localizedPath("/services/retainer", "ru")} replace />}
            />
            <Route
              path="/ru/udalennое-administrirovanie-serverov"
              element={<Navigate to={localizedPath("/services/retainer", "ru")} replace />}
            />
            <Route
              path="/ru/udalennое-administrirovanie-serverov/"
              element={<Navigate to={localizedPath("/services/retainer", "ru")} replace />}
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </LocaleProvider>
    </BrowserRouter>
  </QueryClientProvider>
);

export default App;
